// React and libraries
import React, { useState } from "react";
import {
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
  InputAdornment,
  CircularProgress,
  Box,
  useTheme,
  useMediaQuery,
  Alert,
  Snackbar,
} from "@mui/material";
import { Security, Email } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import Axios from "../../Common/api/axiosConfig";

// Images
import authEffect2 from "../../assets/images/effect-pattern/auth-effect-2.png";
import authEffect from "../../assets/images/effect-pattern/auth-effect.png";
import bgImage from "../../assets/images/new-login-card-bg.png";
import bgImage2 from "../../assets/images/new-login-card-bg2.png";
import bgImageDark from "../../assets/images/new-login-card-drk.png";
import bgImageDark2 from "../../assets/images/new-login-card-drk2.png";

// Main Component
const OtpScreen = () => {
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const email = location.state?.email || "user@example.com";

  // Determine background images based on theme mode
  const leftBgImage = theme.palette.mode === "dark" ? bgImageDark : bgImage;
  const rightBgImage = theme.palette.mode === "dark" ? bgImageDark2 : bgImage2;

  // Handle snackbar close
  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
    setError("");
    setSuccessMessage("");
  };

  // Formik validation schema
  const validation = useFormik({
    initialValues: {
      otp: "",
    },
    validationSchema: Yup.object({
      otp: Yup.string()
        .required("Please enter OTP")
        .matches(/^\d{6}$/, "OTP must be 6 digits"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      setError("");

      try {
        // API call to verify OTP
        const response = await Axios.post("/auth/verify-otp", {
          email: email,
          otp: values.otp,
        });

        const { token, user, type } = response.data;

        // Save auth token and user data to localStorage
        localStorage.setItem("authToken", token);
        localStorage.setItem("userData", JSON.stringify(user));

        console.log("OTP verified successfully");
        console.log("User type:", type);
        console.log("User data:", user);

        // Navigate based on user type
        if (type === "new") {
          navigate("/create-store", { replace: true });
        } else if (type === "old") {
          navigate("/", { replace: true });
        } else {
          // Default navigation if type is not specified
          navigate("/", { replace: true });
        }
      } catch (error: any) {
        // Handle API errors
        if (error.response) {
          const errorMessage =
            error.response.data?.message ||
            error.response.data?.error ||
            "Invalid OTP. Please try again.";
          setError(errorMessage);
          console.error("OTP Verification Error:", error.response.data);
        } else if (error.request) {
          setError(
            "Network error. Please check your connection and try again."
          );
          console.error("Network Error:", error.request);
        } else {
          setError("An unexpected error occurred. Please try again.");
          console.error("Error:", error.message);
        }
        setOpenSnackbar(true);
      } finally {
        setLoading(false);
      }
    },
  });

  const handleChangeEmail = () => {
    navigate("/login");
  };

  const handleResendOtp = async () => {
    setResendLoading(true);
    setError("");

    try {
      // API call to resend OTP
      const response = await Axios.post("/auth/resend-otp", {
        email: email,
      });

      setSuccessMessage("OTP has been resent to your email");
      setOpenSnackbar(true);
      console.log("OTP resent successfully");
    } catch (error: any) {
      if (error.response) {
        const errorMessage =
          error.response.data?.message ||
          error.response.data?.error ||
          "Failed to resend OTP. Please try again.";
        setError(errorMessage);
      } else if (error.request) {
        setError("Network error. Please check your connection and try again.");
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
      setOpenSnackbar(true);
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <>
      {/* Page Title */}
      <title>OTP Verification | Rudrix Store</title>

      {/* Snackbar for notifications */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={error ? "error" : "success"}
          sx={{ width: "100%" }}
        >
          {error || successMessage}
        </Alert>
      </Snackbar>

      {/* Outer container */}
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          p: 2,
          background:
            theme.palette.mode === "dark"
              ? "#1D2127"
              : theme.palette.background.default,
          overflow: "hidden",
        }}
      >
        {/* Decorative background circles */}
        <Box
          sx={{
            position: "absolute",
            top: -50,
            left: -50,
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: theme.palette.primary.light,
            opacity: 0.1,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -80,
            right: -80,
            width: 300,
            height: 300,
            borderRadius: "50%",
            background: theme.palette.secondary.light,
            opacity: 0.1,
          }}
        />

        {/* Card container */}
        <Grid container justifyContent="center" maxWidth="950px">
          <Grid item xs={12} lg={10} xl={9}>
            <Card
              elevation={8}
              sx={{
                borderRadius: 4,
                overflow: "hidden",
                boxShadow: theme.shadows[8],
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "#1D2127"
                    : theme.palette.background.paper,
              }}
            >
              <Grid container spacing={0}>
                {/* Left panel - Image */}
                <Grid
                  item
                  xs={12}
                  md={3}
                  sx={{
                    background: `url(${leftBgImage})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    backgroundColor: theme.palette.primary.main,
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      left: -40,
                      top: -40,
                      opacity: 0.3,
                    }}
                  >
                    <img src={authEffect} alt="Effect" />
                  </Box>
                </Grid>

                {/* Middle panel - OTP Form */}
                <Grid
                  item
                  xs={12}
                  md={6}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "#1D2127"
                        : theme.palette.background.paper,
                  }}
                >
                  <CardContent
                    sx={{
                      p: { xs: 4, md: 6 },
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    {/* Heading */}
                    <Box sx={{ textAlign: "center", mb: 4 }}>
                      <Typography
                        variant="h5"
                        fontWeight={600}
                        color="primary"
                        gutterBottom
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        OTP Verification
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ opacity: 0.8, mb: 2 }}
                      >
                        Enter the 6-digit code sent to your email
                      </Typography>

                      {/* Email display and change option */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ opacity: 0.8 }}
                        >
                          OTP sent to: {email}
                        </Typography>
                        <Button
                          variant="text"
                          size="small"
                          onClick={handleChangeEmail}
                          sx={{
                            minWidth: "auto",
                            textTransform: "none",
                            color: theme.palette.primary.main,
                            fontSize: "0.75rem",
                          }}
                        >
                          Change
                        </Button>
                      </Box>
                    </Box>

                    {/* Form */}
                    <Box
                      component="form"
                      onSubmit={validation.handleSubmit}
                      noValidate
                      sx={{ mt: 2 }}
                    >
                      {/* OTP field */}
                      <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Enter 6-digit OTP"
                        name="otp"
                        label="OTP Code"
                        value={validation.values.otp}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        error={
                          validation.touched.otp &&
                          Boolean(validation.errors.otp)
                        }
                        helperText={
                          validation.touched.otp && validation.errors.otp
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Security
                                color={
                                  validation.touched.otp &&
                                  validation.errors.otp
                                    ? "error"
                                    : "action"
                                }
                              />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          mb: 3,
                          "& .MuiOutlinedInput-root": {
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "#2A2F36"
                                : "transparent",
                            "&:hover fieldset": {
                              borderColor: theme.palette.primary.main,
                            },
                          },
                        }}
                      />

                      {/* Resend OTP option */}
                      <Box sx={{ textAlign: "center", mb: 2 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ opacity: 0.7 }}
                        >
                          Didn't receive code?{" "}
                          <Button
                            variant="text"
                            size="small"
                            onClick={handleResendOtp}
                            disabled={resendLoading}
                            sx={{
                              minWidth: "auto",
                              textTransform: "none",
                              color: theme.palette.primary.main,
                              fontSize: "0.75rem",
                            }}
                          >
                            {resendLoading ? "Sending..." : "Resend OTP"}
                          </Button>
                        </Typography>
                      </Box>

                      {/* Submit button */}
                      <Button
                        fullWidth
                        variant="contained"
                        type="submit"
                        disabled={loading}
                        size="large"
                        sx={{
                          mt: 1,
                          py: 1.5,
                          borderRadius: 2,
                          fontSize: "1rem",
                          fontWeight: 600,
                          backgroundColor: theme.palette.primary.main,
                          background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
                          boxShadow: theme.shadows[4],
                          "&:hover": {
                            boxShadow: theme.shadows[8],
                            backgroundColor: theme.palette.primary.dark,
                            background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`,
                          },
                          "&:disabled": {
                            backgroundColor: theme.palette.action.disabled,
                            background: theme.palette.action.disabledBackground,
                          },
                        }}
                      >
                        {loading ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          "Verify OTP"
                        )}
                      </Button>

                      {/* Footer text */}
                      <Box sx={{ textAlign: "center", mt: 3 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            fontSize: "0.875rem",
                            opacity: 0.7,
                          }}
                        >
                          ©{new Date().getFullYear()} Rudrix. All rights
                          reserved.
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Grid>

                {/* Right panel - Image */}
                <Grid
                  item
                  xs={12}
                  md={3}
                  sx={{
                    background: `url(${rightBgImage})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    backgroundColor: theme.palette.secondary.main,
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      right: -20,
                      bottom: -20,
                      opacity: 0.3,
                      transform: "rotate(45deg)",
                    }}
                  >
                    <img src={authEffect2} alt="Effect 2" />
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default OtpScreen;
